import { useEffect, useState } from 'react'
import { Routes, Route, useLocation, Navigate, useSearchParams } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { Capacitor } from '@capacitor/core'
import { Haptics, NotificationType } from '@capacitor/haptics'

import { dashboardRoutes, kycRoutes, publicRoutes } from '@routes/routes.config'
import OAuthRelayPage from '@/components/pages/OAuth/OAuthRelayPage'

import { SESSION_TOKEN } from '@/utils/constants'
import { useUserStore } from '@/store/useUserStore'
import { useDashboardStore } from '@/store/useDashboardStore'

import dashboardAPI from '@/services/api/dashboard'
import { useAttributionStore } from '@/store/useAttributionStore'

const AppRouter = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const user = useUserStore((state) => state.user)
  const sync = useUserStore((state) => state.sync)
  const setUser = useUserStore((state) => state.setUser)

  const [isInitializing, setIsInitializing] = useState(true)

  const { setAttribution } = useAttributionStore()

  useEffect(() => {
    const utm_source = searchParams.get('utm_source')
    const utm_medium = searchParams.get('utm_medium')
    const utm_campaign = searchParams.get('utm_campaign')
    const utm_term = searchParams.get('utm_term')
    const referrer = document.referrer

    if (utm_source) {
      setAttribution({
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        referrer
      })
    }
  }, [])

  /*
    This delays initial routing render.
    We need to know if user is logged in before rendering the route.
    Or if the session may have expired an need to re-authenticate.
  */
  const financialYear = useDashboardStore((state) => state.financialYear)
  const setDashboard = useDashboardStore((state) => state.setDashboard)

  useEffect(() => {
    const initialize = async () => {
      try {
        if (localStorage.getItem(SESSION_TOKEN)) {
          const userData = await sync()

          if (userData) {
            const dashboard = await dashboardAPI.show({ year: financialYear })

            setDashboard(dashboard)
            setUser(userData)
          }
        }
      } catch {
        /* skip */
      } finally {
        setIsInitializing(false)

        /** Web does not allow without user click to trigger haptics */
        if (Capacitor.isNativePlatform()) {
          Haptics.notification({ type: NotificationType.Success })
        }
      }
    }

    initialize()
  }, [])

  const setNavigationOpen = useDashboardStore(state => state.setNavigationOpen)

  useEffect(() => {
    if (location.pathname === "/" ||
      location.pathname === "/expenses" ||
      location.pathname === "/income" ||
      location.pathname === "/taxes"
    ) {
      setNavigationOpen(true)
    }

    if (location.pathname.startsWith("/bolt/") ||
      location.pathname.startsWith("/expenses/")
    ) {
      setNavigationOpen(false)
    }
  }, [location.pathname])

  if (isInitializing) { return <></> }

  /*
    When switching to private routes it will fail to exit animations.

    Switch variable:
    When user is mfa enabled it gives dashboard routes.
    When user has not mfa enabled it gives kyc routes.
    When there is no user it gives public routes.
  */

  const routes = user?.mfa ? dashboardRoutes : user ? kycRoutes : publicRoutes

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/oauth/relay" element={<OAuthRelayPage />} />

        {routes.map(({ path, component: Component }) => (
          <Route
            key={path}
            path={path}
            element={<Component />}
          />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  )
}

export default AppRouter